// Phase 2.12 Tier 3 Step 22 / 25 — manage-templates modal Stimulus
// controller. Mirrors `portfolio_preset_manage_controller` shape:
// JSON-only CRUD against `/company_settings/dashboard_layout_templates`,
// optimistic UI updates, server-validation surfacing.
//
// Surfaces:
//   - List existing templates (system rows shown as read-only, custom
//     rows editable by their author or any captain/commander).
//   - Save the user's current dashboard layout as a new template
//     (captures `widgets-json` from the dashboard-grid controller).
//   - Apply a template to the current dashboard with one click.
//   - Rename / set-default / delete a custom template.
//
// The modal is opened by `dashboard-templates#openManage`; the
// `apply` action posts the template id to a small handler on the
// dashboard controller that calls the resolver and reloads the grid.

import { Controller } from "@hotwired/stimulus"
import { confirmAction } from "../lib/confirm"
import { showToast } from "../lib/toast"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Maps server error codes to friendlier copy. Anything not in this
// map shows through as-is, so adding a new server-side error doesn't
// silently swallow useful diagnostics.
const ERROR_MESSAGES = {
  not_authorized:  "You don't have permission to do that.",
  system_template: "System templates can't be edited or deleted.",
  invalid_scope:   "Template scope is invalid.",
  invalid_role:    "Template role is invalid.",
  forbidden:       "Action not allowed."
}

function friendlyError(errors, fallback) {
  const list = Array.isArray(errors) ? errors : [errors].filter(Boolean)
  if (!list.length) return fallback
  return list.map((e) => ERROR_MESSAGES[e] || e).join(", ")
}

export default class extends Controller {
  static values = {
    listUrl: String,        // GET   /company_settings/dashboard_layout_templates
    createUrl: String,      // POST  /company_settings/dashboard_layout_templates
    destroyUrl: String,     // DELETE /...
    setDefaultUrl: String,  // POST  /.../:id/set_default
    promoteUrl: String,     // POST  /.../:id/promote
    scope: String,          // "project" | "portfolio"
    csrfToken: String,
    canManageShared: Boolean,
    isFounder: Boolean
  }

  static targets = ["modal", "list", "saveName", "saveScopeSelect", "saveRoleSelect", "quickList", "tabsList"]

  // Phase 2.12 Follow-Up 1 (Path A) — same cap as
  // `parse_and_validate_tabs` server-side. Kept in sync to keep error
  // surfacing client-side instead of round-tripping a 422.
  static MAX_TABS = 8

  open(event) {
    event?.preventDefault()
    this._refreshList()
    this.modalTarget.classList.remove("hidden")
  }

  // Lazily load the quick-access dropdown when the user opens it.
  // Mirrors `_refreshList` but renders into the dropdown menu with a
  // simpler row markup (apply on click + ★ + currently-applied marker).
  async prefetch(event) {
    if (!this.hasQuickListTarget || !this.hasListUrlValue) return
    if (this._quickLoaded) return

    try {
      const url = `${this.listUrlValue}?scope=${encodeURIComponent(this.scopeValue || "project")}`
      const res = await fetch(url, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this._renderQuickList(data.templates || [])
      this._quickLoaded = true
    } catch (err) {
      console.warn("[dashboard-layout-templates] quick prefetch failed", err)
      this.quickListTarget.innerHTML = `<p class="text-[12px] text-red-600 px-3 py-2">Failed to load.</p>`
    }
  }

  _renderQuickList(templates) {
    if (!this.hasQuickListTarget) return

    const currentSlug = new URLSearchParams(window.location.search).get("template")
    const rows = templates.map((t) => {
      const isApplied = currentSlug === t.slug
      const star = t.is_default
        ? `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" class="w-3.5 h-3.5 text-amber-500 ml-1 inline-block shrink-0"><path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.32.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.32-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/></svg>`
        : ""
      const checkmark = isApplied
        ? `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5 text-brand shrink-0 ml-2"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>`
        : ""
      const sysBadge = t.system ? `<span class="ml-1 px-1 py-0.5 rounded bg-gray-100 text-gray-500 text-[9px] font-medium uppercase tracking-wide">SYS</span>` : ""
      return `<button type="button"
                      data-action="click->dashboard-layout-templates#apply"
                      data-template-slug="${this._escape(t.slug)}"
                      class="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-gray-700 hover:bg-gray-50 cursor-pointer ${isApplied ? "bg-brand/5" : ""}">
        <span class="flex items-center gap-1 min-w-0">
          <span class="truncate">${this._escape(t.name)}</span>
          ${star}${sysBadge}
        </span>
        ${checkmark}
      </button>`
    }).join("")

    this.quickListTarget.innerHTML = `
      ${rows || '<p class="text-[12px] text-gray-400 px-3 py-2">No templates yet.</p>'}
      <div class="border-t border-gray-100 mt-1 pt-1">
        <button type="button"
                data-action="click->dashboard-layout-templates#open"
                class="w-full flex items-center gap-1.5 px-3 py-1.5 text-left text-gray-600 hover:bg-gray-50 cursor-pointer text-[12px]">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          Manage templates…
        </button>
      </div>
    `
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  // Backdrop close — wired via data-action on the centering wrapper.
  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async _refreshList() {
    if (!this.hasListUrlValue) return

    // Invalidate quick-list cache so the dropdown reflects mutations
    // (save / delete / set-default) on its next open.
    this._quickLoaded = false

    try {
      const url = `${this.listUrlValue}?scope=${encodeURIComponent(this.scopeValue || "project")}`
      const res = await fetch(url, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this._renderList(data.templates || [])
    } catch (err) {
      console.warn("[dashboard-layout-templates] list failed", err)
      if (this.hasListTarget) {
        this.listTarget.innerHTML = `<p class="text-[13px] text-red-600 px-3 py-2">Failed to load templates.</p>`
      }
    }
  }

  _renderList(templates) {
    if (!this.hasListTarget) return

    if (templates.length === 0) {
      this.listTarget.innerHTML = `<p class="text-[13px] text-gray-500 px-3 py-4">No templates yet. Save the current view to create your first template.</p>`
      return
    }

    this.listTarget.innerHTML = templates.map((t) => this._templateRow(t)).join("")
  }

  _templateRow(t) {
    const editable = t.editable && !t.system
    const badges = []
    if (t.system) badges.push(`<span class="ml-1 px-1 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-medium">System</span>`)
    if (t.is_default) badges.push(`<span class="ml-1 px-1 py-0.5 rounded bg-brand/10 text-brand text-[10px] font-medium">Default</span>`)
    if (t.role) badges.push(`<span class="ml-1 px-1 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">${this._escape(t.role)}</span>`)

    const actions = []
    // Set-as-default is allowed on every row including system templates
    // (per-company is_default flag — each company has its own seeded
    // copy so flipping it only affects this tenant).
    const starColor = t.is_default ? "text-amber-500" : "text-gray-400 hover:text-brand"
    const starFill = t.is_default ? "currentColor" : "none"
    actions.push(`<button type="button" data-action="click->dashboard-layout-templates#stopPropagation click->dashboard-layout-templates#setDefault" data-template-id="${t.id}" class="p-1 ${starColor} rounded hover:bg-gray-100" title="${t.is_default ? "Currently the company default" : "Set as company default"}">
      <svg xmlns="http://www.w3.org/2000/svg" fill="${starFill}" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.32.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.32-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>
    </button>`)
    if (editable) {
      actions.push(`<button type="button" data-action="click->dashboard-layout-templates#stopPropagation click->dashboard-layout-templates#destroy" data-template-id="${t.id}" class="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50" title="Delete">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
      </button>`)
    }

    if (this.isFounderValue && !t.system) {
      actions.push(`<button type="button" data-action="click->dashboard-layout-templates#stopPropagation click->dashboard-layout-templates#promote" data-template-id="${t.id}" class="px-1.5 py-0.5 ml-1 rounded bg-purple-50 text-purple-700 text-[10px] font-medium hover:bg-purple-100" title="Promote to system template (founder only)">Promote</button>`)
    }

    return `<li class="flex items-center gap-3 px-3 py-2 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
               data-template-id="${t.id}"
               data-template-slug="${this._escape(t.slug)}"
               data-action="click->dashboard-layout-templates#apply"
               title="Apply this template">
      <div class="flex-1 min-w-0">
        <div class="text-[13px] font-medium text-gray-900 truncate">
          ${this._escape(t.name)}
          ${badges.join("")}
        </div>
        <div class="text-[11px] text-gray-500">${this._escape(t.scope)} · ${(t.widgets || []).length} widget${(t.widgets || []).length === 1 ? "" : "s"}</div>
      </div>
      ${actions.join("")}
    </li>`
  }

  async saveAsTemplate(event) {
    event?.preventDefault()

    const name = (this.hasSaveNameTarget && this.saveNameTarget.value || "").trim()
    if (!name) {
      showToast("Name your template before saving.", "error")
      return
    }

    const widgets = this._collectCurrentWidgets()
    if (!widgets.length) {
      showToast("Pick at least one widget for this template.", "error")
      return
    }

    const role = this.hasSaveRoleSelectTarget ? this.saveRoleSelectTarget.value : ""
    const scope = this.hasSaveScopeSelectTarget ? this.saveScopeSelectTarget.value : (this.scopeValue || "project")

    const tabs = this._collectCurrentTabs()
    const tabsError = this._validateTabsClient(tabs, widgets)
    if (tabsError) {
      showToast(tabsError, "error")
      return
    }

    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          name: name,
          scope: scope,
          role: role || null,
          widgets: widgets,
          tabs: tabs
        })
      })
      const data = await res.json()
      if (!res.ok || data.success === false) {
        showToast(friendlyError(data.errors, "Save failed"), "error")
        return
      }
      showToast("Template saved")
      this._trackTabsSaved(data.template?.slug, tabs)
      if (this.hasSaveNameTarget) this.saveNameTarget.value = ""
      if (this.hasTabsListTarget) this.tabsListTarget.innerHTML = ""
      this._refreshList()

      // On portfolio scope, immediately apply the new template so the
      // user sees their custom layout instead of having to click it
      // from the list.
      const newSlug = data.template?.slug
      if (newSlug && this.scopeValue === "portfolio") {
        const url = new URL(window.location.href)
        url.searchParams.set("template", newSlug)
        window.location.href = url.toString()
      }
    } catch (err) {
      console.warn("[dashboard-layout-templates] save failed", err)
      showToast("Save failed.", "error")
    }
  }

  async apply(event) {
    const slug = event.currentTarget.dataset.templateSlug
    if (!slug) return

    const url = new URL(window.location.href)
    url.searchParams.set("template", slug)
    window.location.href = url.toString()
  }

  async setDefault(event) {
    const id = event.currentTarget.dataset.templateId
    if (!id) return

    try {
      const url = this.setDefaultUrlValue.replace(/:id/, id)
      const res = await fetch(url, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || data.success === false) {
        showToast(friendlyError(data.errors, "Could not set default"), "error")
        return
      }
      // The cascade is "URL > user layout > company default > system
      // default" — so a freshly-set company default won't apply to
      // anyone with a personal user layout (they'd shadow it). Tell
      // the user instead of leaving them confused about why their own
      // dashboard didn't change.
      if (data.user_has_personal_layout) {
        showToast("Default updated. Click 'Restore default view' in Customize to apply it to your own dashboard.")
      } else {
        showToast("Default updated")
      }
      this._refreshList()
    } catch (err) {
      console.warn("[dashboard-layout-templates] set-default failed", err)
      showToast("Could not set default.", "error")
    }
  }

  async destroy(event) {
    const id = event.currentTarget.dataset.templateId
    if (!id) return
    const ok = await confirmAction({
      title: "Delete template?",
      message: "This template will be removed for everyone in this company. This action cannot be undone.",
      confirmLabel: "Delete",
      danger: true
    })
    if (!ok) return

    try {
      const url = `${this.listUrlValue}/${id}`
      const res = await fetch(url, {
        method: "DELETE",
        headers: this._jsonHeaders(),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || data.success === false) {
        showToast(friendlyError(data.errors, "Delete failed"), "error")
        return
      }
      showToast("Template deleted")
      this._refreshList()
    } catch (err) {
      console.warn("[dashboard-layout-templates] destroy failed", err)
      showToast("Delete failed.", "error")
    }
  }

  async promote(event) {
    const id = event.currentTarget.dataset.templateId
    if (!id) return
    const ok = await confirmAction({
      title: "Promote to system template?",
      message: "This template will become a seeded system row, available to every company. Only ConsLog founders can promote.",
      confirmLabel: "Promote"
    })
    if (!ok) return

    try {
      const url = this.promoteUrlValue.replace(/:id/, id)
      const res = await fetch(url, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || data.success === false) {
        showToast(friendlyError(data.errors, "Promote failed"), "error")
        return
      }
      showToast("Template promoted to system")
      this._refreshList()
    } catch (err) {
      console.warn("[dashboard-layout-templates] promote failed", err)
    }
  }

  // ── Phase 2.12 Follow-Up 1 (Path A) — Tabs editor ─────────────────

  // "+ Add tab" — appends a new empty tab row. Capped at MAX_TABS to
  // match the server-side `parse_and_validate_tabs` guard.
  addTab(event) {
    event?.preventDefault()
    if (!this.hasTabsListTarget) return

    const existing = this.tabsListTarget.querySelectorAll("[data-tab-row]").length
    if (existing >= this.constructor.MAX_TABS) {
      showToast(`At most ${this.constructor.MAX_TABS} tabs per template.`, "error")
      return
    }

    const widgets = this._collectCurrentWidgets()
    if (!widgets.length) {
      showToast("Add widgets to the dashboard before defining tabs.", "error")
      return
    }

    this.tabsListTarget.appendChild(this._buildTabRow({ label: "", widget_keys: [] }, widgets))
  }

  // Per-row delete button. The Dashboard Tab Deleted Mixpanel event
  // fires only on save (Path A is declarative — adding/removing tab
  // rows in this modal isn't a "deletion" until the template is
  // saved). Path B fires deletion events on the live dashboard.
  removeTab(event) {
    event.currentTarget.closest("[data-tab-row]")?.remove()
  }

  // Walks the rendered tab rows and serializes them into the
  // `[{label, widget_keys}]` shape the controller expects.
  _collectCurrentTabs() {
    if (!this.hasTabsListTarget) return []

    return Array.from(this.tabsListTarget.querySelectorAll("[data-tab-row]")).map((row) => {
      const label = (row.querySelector("[data-tab-label-input]")?.value || "").trim()
      const widget_keys = Array.from(row.querySelectorAll("[data-tab-widget-checkbox]:checked"))
        .map((cb) => cb.value)
      return { label, widget_keys }
    }).filter((t) => t.label.length > 0)
  }

  // Mirrors the server's `parse_and_validate_tabs` so the user gets
  // immediate feedback. Server still validates; this is just to keep
  // the round-trip cost down for the obvious cases.
  _validateTabsClient(tabs, widgets) {
    if (!tabs.length) return null
    if (tabs.length > this.constructor.MAX_TABS) {
      return `Too many tabs (limit ${this.constructor.MAX_TABS}).`
    }

    const labels = tabs.map((t) => t.label.toLowerCase())
    const dup = labels.find((l, i) => labels.indexOf(l) !== i)
    if (dup) return `Duplicate tab name: "${dup}".`

    const widgetKeys = new Set(widgets.map((w) => String(w.widget_type || w.key || "")))
    for (const t of tabs) {
      if (!t.widget_keys.length) {
        return `Tab "${t.label}" has no widgets — assign at least one or remove the tab.`
      }
      const missing = t.widget_keys.filter((k) => !widgetKeys.has(k))
      if (missing.length) {
        return `Tab "${t.label}" references widgets not in this template: ${missing.join(", ")}.`
      }
    }

    return null
  }

  // Fires a `Dashboard Tab Created` event per saved tab and a
  // `Dashboard Widget Tab Assigned` event per (tab, widget) pair the
  // user assigned. Path A is declarative — we only know the final
  // state on save, so emitting per-row interaction events would be
  // noisy. Path B (edit mode) wires per-action events on the live
  // dashboard for finer-grained funnels.
  _trackTabsSaved(templateSlug, tabs) {
    if (!Array.isArray(tabs) || tabs.length === 0) return

    tabs.forEach((tab) => {
      mixpanelTrack("Dashboard Tab Created", {
        template_slug: templateSlug || null,
        tab_label: tab.label,
        widget_count: tab.widget_keys.length,
        surface: "templates_modal"
      })
      tab.widget_keys.forEach((widgetType) => {
        mixpanelTrack("Dashboard Widget Tab Assigned", {
          template_slug: templateSlug || null,
          widget_type: widgetType,
          tab_label: tab.label,
          assigned: true,
          surface: "templates_modal"
        })
      })
    })
  }

  // Builds a single tab row's DOM. The widget chip-checkbox grid is
  // sourced from the live widget set (passed in), so a tab assignment
  // can never reference a widget that isn't on the dashboard — the
  // exact constraint the server validates.
  _buildTabRow(tab, widgets) {
    const row = document.createElement("div")
    row.dataset.tabRow = ""
    row.className = "border border-gray-200 rounded-md p-2 space-y-2 bg-white"

    const labelRow = document.createElement("div")
    labelRow.className = "flex items-center gap-2"
    labelRow.innerHTML = `
      <input type="text"
             data-tab-label-input
             value="${this._escape(tab.label || "")}"
             maxlength="60"
             placeholder="Tab name"
             class="flex-1 px-2 py-1 text-[13px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" />
      <button type="button"
              data-action="click->dashboard-layout-templates#removeTab"
              class="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
              title="Remove tab">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `
    row.appendChild(labelRow)

    const grid = document.createElement("div")
    grid.className = "flex flex-wrap gap-1.5"
    const assigned = new Set((tab.widget_keys || []).map(String))
    widgets.forEach((w) => {
      const key = String(w.widget_type || w.key || "")
      if (!key) return
      const checked = assigned.has(key) ? "checked" : ""
      const id = `tab-widget-${Math.random().toString(36).slice(2, 9)}`
      const chip = document.createElement("label")
      chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 text-[11px] text-gray-700 cursor-pointer has-[:checked]:bg-brand/10 has-[:checked]:border-brand has-[:checked]:text-brand"
      chip.innerHTML = `
        <input type="checkbox"
               id="${id}"
               value="${this._escape(key)}"
               data-tab-widget-checkbox
               ${checked}
               class="sr-only" />
        <span>${this._escape(this._widgetTitleFor(key) || key)}</span>
      `
      grid.appendChild(chip)
    })
    row.appendChild(grid)

    return row
  }

  // Reads the human-readable widget title from the live DOM. Both
  // the project-side `_widget_wrapper.html.erb` and the portfolio-
  // side `_widgets_grid.html.erb` emit `data-widget-title`. Falls
  // back to the raw key when the lookup fails (defensive — never
  // render a chip with no label).
  _widgetTitleFor(key) {
    const el = document.querySelector(`[data-widget-type="${CSS.escape(key)}"]`)
    return el?.dataset.widgetTitle || null
  }

  _collectCurrentWidgets() {
    // Prefer the live DOM (so a user can re-arrange in edit mode and
    // capture the new order); fall back to the server-rendered initial
    // layout when no widget cards are present yet.
    const cards = document.querySelectorAll('[data-dashboard-grid-target="widget"]')
    if (cards.length) {
      return Array.from(cards).map((el, index) => ({
        widget_type: el.dataset.widgetType,
        col_span: parseInt(el.dataset.widgetColSpan, 10) || 1,
        position: index,
        settings: this._parseJSON(el.dataset.widgetSettings) || {}
      })).filter((w) => Boolean(w.widget_type))
    }

    const grid = document.querySelector('[data-controller~="dashboard-grid"]')
    if (grid) return this._parseJSON(grid.dataset.dashboardGridLayoutValue) || []

    // Phase 2.12 Tier 3 — portfolio dashboard has its own widgets_grid
    // partial that doesn't use the project-dashboard layout DOM. Read
    // the JSON manifest the partial sets on its wrapper.
    const portfolioGrid = document.querySelector("[data-portfolio-widgets]")
    if (portfolioGrid) return this._parseJSON(portfolioGrid.dataset.portfolioWidgets) || []

    return []
  }

  _parseJSON(raw) {
    if (!raw) return null
    try { return JSON.parse(raw) } catch { return null }
  }

  _jsonHeaders() {
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    }
  }

  _escape(str) {
    return String(str || "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
